import React, {
    CSSProperties, FC, memo, useEffect, useLayoutEffect, useRef, useState,
} from 'react';

import { PdfViewerClient } from '../client/pdfViewerClient';
import { PageTileLayers } from '../client/pageTileLayers';
import { ZoomController } from '../client/zoomController';
import { TILE_DELAY_MS } from '../constants';
import { drawBitmap } from '../helpers/bitmap';
import { emailWatermarkPlugin, imageWatermarkPlugin } from '../helpers/watermarks';
import { quantizeOverviewWidth } from '../helpers/zoom';
import { Observe } from '../hooks/useSharedObserver';
import { Watermarks } from '../hooks/useWatermarks';
import { PageInfo, Rect } from '../types';
import styles from '../PdfTileViewer.module.scss';

export interface Highlight {
    rect: Rect;
    isActive: boolean;
}

interface PageViewProps {
    index: number;
    page: PageInfo;
    controller: ZoomController;
    client: PdfViewerClient;
    watermarks: Watermarks;
    highlights: Highlight[];
    observeNear: Observe;
    observeVisible: Observe;
}

const noop = (): void => undefined;

const PageView: FC<PageViewProps> = ({
    index, page, controller, client, watermarks, highlights, observeNear, observeVisible,
}) => {
    const boxRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const overviewRef = useRef<HTMLCanvasElement>(null);
    const [isNear, setIsNear] = useState<boolean>(false);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [overview, setOverview] = useState<ImageBitmap | null>(null);
    const [settledZoom, setSettledZoom] = useState<number>(controller.zoom);

    const overviewWidth = quantizeOverviewWidth(page.w * controller.zoom);

    useEffect(() => observeNear(boxRef.current, setIsNear), [observeNear]);
    useEffect(() => observeVisible(boxRef.current, setIsVisible), [observeVisible]);

    useEffect(() => {
        if (!isNear) {
            setOverview(null);
            return undefined;
        }
        let cancelled = false;
        client.overview(index, quantizeOverviewWidth(page.w * controller.zoom))
            .then((bitmap) => !cancelled && bitmap && setOverview(bitmap))
            .catch(noop);
        const unsubscribe = controller.onSettle(() => setSettledZoom(controller.zoom));
        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [isNear, index, client, controller]);

    useEffect(() => {
        if (!isVisible) {
            return undefined;
        }
        const layers = new PageTileLayers({
            box: boxRef.current,
            client,
            index,
            page,
            getZoom: () => controller.renderTarget,
            layerClass: styles.layer,
            pendingClass: styles.pending,
        });
        const timer = window.setTimeout(() => layers.mount(), TILE_DELAY_MS);
        const unsubscribeSettle = controller.onSettle(() => layers.settle());
        const unsubscribeTarget = controller.onTarget(() => layers.settle());
        return () => {
            window.clearTimeout(timer);
            unsubscribeSettle();
            unsubscribeTarget();
            layers.stop();
        };
    }, [isVisible, client, controller, index, page]);

    const canvasWidth = Math.round(page.w * settledZoom);
    const canvasHeight = Math.round(page.h * settledZoom);

    useEffect(() => {
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) {
            return;
        }
        const size = { width: canvasWidth, height: canvasHeight };
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
        imageWatermarkPlugin(watermarks.logo, ctx, size);
        if (watermarks.email) {
            emailWatermarkPlugin(watermarks.email, ctx, size);
        }
    }, [isNear, canvasWidth, canvasHeight, watermarks]);

    const overviewBitmap = isNear ? (overview ?? client.peekOverview(index, overviewWidth)) : undefined;

    useLayoutEffect(() => {
        if (overviewBitmap && overviewRef.current) {
            drawBitmap(overviewRef.current, overviewBitmap);
        }
    }, [overviewBitmap]);

    return (
        <div
            ref={boxRef}
            className={styles.page}
            style={{ '--w': page.w, '--h': page.h } as CSSProperties}
        >
            {overviewBitmap && <canvas ref={overviewRef} className={styles.overview} />}
            {isNear && (
                <canvas
                    ref={canvasRef}
                    className={styles.watermark}
                    width={canvasWidth}
                    height={canvasHeight}
                />
            )}
            {highlights.length > 0 && (
                <div className={styles.highlights}>
                    {highlights.map(({ rect: [top, left, w, h], isActive }) => (
                        <div
                            key={`${top}-${left}-${w}-${h}`}
                            className={isActive ? `${styles.highlight} ${styles.active}` : styles.highlight}
                            style={{
                                left, top, width: w, height: h,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default memo(PageView);
